import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { UserProgress, SkillLevels, MoveSkillMetrics, PieceMoveSkills } from '../types/progress';
import { Mission, MissionResult, MissionCompletionData } from '../types/mission';
import localStorageService, { UserProgressUpdate } from '../services/storage/localStorageService';
import { getUnlockedAchievements, getNewlyUnlockedAchievements } from '../data/achievements';
import { useLearning } from './LearningContext';
import { 
  calculateSkillDistribution, 
  identifyWeakAreas, 
  analyzeTacticalThemeProficiency 
} from '../services/learning/learningMetricsService';
import { SkillDistribution, WeakArea, TacticalThemeProficiency, KnowledgeItem, SuperMemoGrade } from '../types/learning';
import { updateKnowledgeItemReview } from '../services/learning/spacedRepetitionService';
import { knowledgeItemService } from '../services/storage/knowledgeItemService';

/**
 * Challenge completion data
 */
interface Challenge {
  id: string;
  type: string;
  score: number;
  completedAt: string;
}

/**
 * Extend UserProgress to include completed challenges
 */
interface ExtendedUserProgress extends UserProgress {
  completedChallenges: Challenge[];
}

/**
 * Move attempt tracking data
 */
interface MoveAttempt {
  pieceType: 'pawn' | 'knight' | 'bishop' | 'rook' | 'queen' | 'king';
  moveType: 'normal' | 'castling' | 'enPassant' | 'promotion';
  isSuccessful: boolean;
  timeSpent: number;
  usedHint: boolean;
}

/**
 * Interface for the Progress context
 * Defines methods available to consumers
 */
interface ProgressContextType {
  userProgress: ExtendedUserProgress;
  updateProgress: (updates: UserProgressUpdate) => void;
  completeMission: (missionId: string, results: MissionCompletionData) => void;
  updateSkill: (skillName: keyof SkillLevels, amount: number) => void;
  completeChallenge: (challenge: Challenge) => void;
  resetProgress: () => void;
  isLoading: boolean;
  unlockedAchievements: string[];
  checkForNewAchievements: () => string[];
  // New methods for move skill tracking
  trackMoveAttempt: (moveAttempt: MoveAttempt) => void;
  getMoveSkillLevel: (
    pieceType: 'pawn' | 'knight' | 'bishop' | 'rook' | 'queen' | 'king', 
    moveType?: 'normal' | 'castling' | 'enPassant' | 'promotion'
  ) => number;
  updateMoveSkill: (
    pieceType: 'pawn' | 'knight' | 'bishop' | 'rook' | 'queen' | 'king', 
    moveType: 'normal' | 'castling' | 'enPassant' | 'promotion', 
    amount: number
  ) => void;
  getMoveSkillMetrics: () => MoveSkillMetrics;
  // New sequence challenge method
  completeSequenceChallenge: (item: KnowledgeItem, grade: SuperMemoGrade) => void;
  // Metrics
  detailedSkillDistribution: SkillDistribution;
  weakAreas: WeakArea[];
  tacticalProficiency: TacticalThemeProficiency;
}

/**
 * Interface for tracking move attempts
 */
interface TrackMoveOptions {
  piece?: keyof PieceMoveSkills;
  moveType?: 'normal' | 'castling' | 'enPassant' | 'promotion';
  timeSpent?: number;
  usedHint?: boolean;
}

/**
 * Create the context with undefined initial value
 */
const ProgressContext = createContext<ProgressContextType | undefined>(undefined);

// Default piece move skills
const DEFAULT_PIECE_MOVES: PieceMoveSkills = {
  pawn: 1,
  knight: 1,
  bishop: 1,
  rook: 1,
  queen: 1,
  king: 1,
};

/**
 * Provider component that wraps the app and makes progress data available
 */
export const ProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // State for user progress - cast to ExtendedUserProgress
  const [userProgress, setUserProgress] = useState<ExtendedUserProgress>({
    ...localStorageService.getUserProgress(),
    completedChallenges: []
  });
  
  // State for tracking loading state
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // State for tracking unlocked achievements
  const [unlockedAchievements, setUnlockedAchievements] = useState<string[]>([]);

  // Get knowledge items from LearningContext
  const { knowledgeItems, isLoading: isLearningLoading } = useLearning();

  // Calculate learning metrics when knowledge items or user progress changes
  const detailedSkillDistribution = useMemo(() => {
    if (isLearningLoading || !knowledgeItems || knowledgeItems.length === 0) {
      return {};
    }
    return calculateSkillDistribution(knowledgeItems);
  }, [knowledgeItems, isLearningLoading]);

  const weakAreas = useMemo(() => {
    if (isLearningLoading || !knowledgeItems || knowledgeItems.length === 0) {
      return [];
    }
    return identifyWeakAreas(knowledgeItems);
  }, [knowledgeItems, isLearningLoading]);

  const tacticalProficiency = useMemo(() => {
    if (isLearningLoading || !knowledgeItems || knowledgeItems.length === 0) {
      return {};
    }
    return analyzeTacticalThemeProficiency(knowledgeItems);
  }, [knowledgeItems, isLearningLoading]);

  // Load user progress from localStorage on component mount
  useEffect(() => {
    try {
      const progress = localStorageService.getUserProgress();
      setUserProgress({
        ...progress,
        completedChallenges: []
      });
      
      // Initialize unlocked achievements
      const initialUnlockedAchievements = getUnlockedAchievements(progress) || [];
      setUnlockedAchievements(initialUnlockedAchievements.map(a => a.id));
    } catch (error) {
      // Only log errors in non-test environments to keep test output clean
      if (process.env.NODE_ENV !== 'test') {
        console.error('Error loading progress data:', error);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save user progress whenever it changes
  useEffect(() => {
    if (!isLoading) {
      const progressToSave: UserProgress = {
        ...userProgress,
        unlockedAchievements
      };
      localStorageService.saveUserProgress(progressToSave);
    }
  }, [userProgress, unlockedAchievements, isLoading]);

  /**
   * Check for newly unlocked achievements without updating state
   * This breaks the recursive cycle by not calling updateProgress 
   * 
   * @param currentProgress The current progress to check against
   * @param currentUnlockedAchievements The current unlocked achievements
   * @returns Object containing newly unlocked achievement IDs and rewards
   */
  const checkNewAchievementsWithoutUpdate = (
    currentProgress: UserProgress, 
    currentUnlockedAchievements: string[]
  ): { newAchievementIds: string[], skillPointRewards: Partial<SkillLevels> } => {
    // Ensure we get a valid array, even if getNewlyUnlockedAchievements has an issue
    const newlyUnlocked = getNewlyUnlockedAchievements(currentProgress, currentUnlockedAchievements);
    
    // Early return with empty results if no achievements or invalid result
    if (!Array.isArray(newlyUnlocked) || newlyUnlocked.length === 0) {
      return { 
        newAchievementIds: [], 
        skillPointRewards: {} 
      };
    }
    
    // Process valid achievements
    const newAchievementIds = newlyUnlocked.map(a => a.id);
    
    // Collect all skill point rewards
    let combinedSkillRewards: Partial<SkillLevels> = {};
    
    newlyUnlocked.forEach(achievement => {
      if (achievement && achievement.rewards?.skillPoints) {
        // Merge all skill point rewards
        combinedSkillRewards = {
          ...combinedSkillRewards,
          ...achievement.rewards.skillPoints
        };
      }
    });
    
    return { 
      newAchievementIds, 
      skillPointRewards: combinedSkillRewards 
    };
  };

  /**
   * Update user progress with new data
   * 
   * @param updates Partial progress object with fields to update
   */
  const updateProgress = (updates: UserProgressUpdate) => {
    // Apply the initial updates
    const updatedProgress = localStorageService.updateUserProgress(updates);
    
    // Check for new achievements based on the updated progress state
    const { newAchievementIds, skillPointRewards } = checkNewAchievementsWithoutUpdate(
      updatedProgress,
      unlockedAchievements
    );
    
    // If we have new achievements with skill rewards, apply them in a single update
    if (newAchievementIds.length > 0) {
      // Update unlocked achievements state
      setUnlockedAchievements(prev => [...prev, ...newAchievementIds]);
      
      // If there are skill rewards to apply
      if (Object.keys(skillPointRewards).length > 0) {
        // Create merged skill levels including both original updates and achievement rewards
        const mergedSkillLevels = {
          ...updatedProgress.skillLevels
        };
        
        // Apply each skill reward, ensuring proper handling of pieceMoves
        Object.entries(skillPointRewards).forEach(([skillName, points]) => {
          const skill = skillName as keyof SkillLevels;
          
          if (skill === 'pieceMoves' && typeof points === 'object') {
            // Handle piece moves object separately
            mergedSkillLevels.pieceMoves = {
              ...(mergedSkillLevels.pieceMoves || DEFAULT_PIECE_MOVES),
              ...points
            };
          } else if (typeof mergedSkillLevels[skill] === 'number' && typeof points === 'number') {
            // Handle numeric skills
            (mergedSkillLevels[skill] as number) = Math.min(100, (mergedSkillLevels[skill] as number) + points);
          }
        });
        
        // Apply the final merged updates to localStorage
        const finalUpdatedProgress = localStorageService.updateUserProgress({
          skillLevels: mergedSkillLevels
        });
        
        // Update the state with the final progress
        setUserProgress({
          ...finalUpdatedProgress,
          completedChallenges: userProgress.completedChallenges || []
        });
      } else {
        // If no skill rewards, just update with the initial updates
        setUserProgress({
          ...updatedProgress,
          completedChallenges: userProgress.completedChallenges || []
        });
      }
    } else {
      // If no new achievements, just update with the initial updates
      setUserProgress({
        ...updatedProgress,
        completedChallenges: userProgress.completedChallenges || []
      });
    }
  };

  /**
   * Check for newly unlocked achievements
   * Public method that maintains backwards compatibility
   * 
   * @returns Array of newly unlocked achievement IDs
   */
  const checkForNewAchievements = (): string[] => {
    const { newAchievementIds, skillPointRewards } = checkNewAchievementsWithoutUpdate(
      userProgress,
      unlockedAchievements
    );
    
    if (newAchievementIds.length > 0) {
      // Update unlocked achievements state
      setUnlockedAchievements(prev => [...prev, ...newAchievementIds]);
      
      // If there are skill rewards, apply them WITHOUT recursive updateProgress calls
      if (Object.keys(skillPointRewards).length > 0) {
        // Apply skill rewards directly to localStorage without triggering the full updateProgress
        const updatedSkillLevels = { ...userProgress.skillLevels };
        
        Object.entries(skillPointRewards).forEach(([skillName, points]) => {
          const skill = skillName as keyof SkillLevels;
          
          if (skill === 'pieceMoves' && typeof points === 'object') {
            updatedSkillLevels.pieceMoves = {
              ...(updatedSkillLevels.pieceMoves || DEFAULT_PIECE_MOVES),
              ...points
            };
          } else if (typeof updatedSkillLevels[skill] === 'number' && typeof points === 'number') {
            (updatedSkillLevels[skill] as number) = Math.min(100, (updatedSkillLevels[skill] as number) + points);
          }
        });
        
        // Update userProgress state directly instead of calling updateProgress
        setUserProgress(prev => ({
          ...prev,
          skillLevels: updatedSkillLevels
        }));
        
        // Update localStorage
        localStorageService.saveUserProgress({
          ...userProgress,
          skillLevels: updatedSkillLevels,
          unlockedAchievements: [...unlockedAchievements, ...newAchievementIds]
        });
      }
      
      return newAchievementIds;
    }
    
    return [];
  };

  /**
   * Complete a mission and update progress accordingly
   * Now accepts a unified results object.
   * 
   * @param missionId ID of the completed mission
   * @param results Object containing all performance metrics for the mission
   */
  const completeMission = (missionId: string, results: MissionCompletionData) => {
    // Extract data from results object
    const { success, score, accuracy, timeSpent, movesUsed, hintsUsed } = results;
    
    // Only proceed if successful
    if (!success) return;

    // Create the mission result, potentially including new fields
    const missionResult: MissionResult = {
      missionId,
      completed: true,
      score,
      accuracy,
      timeSpent,
      // Add optional fields if they exist and have meaningful values
      ...(movesUsed !== undefined && movesUsed > 0 && { movesUsed }),
      ...(hintsUsed !== undefined && hintsUsed > 0 && { hintsUsed }),
      completedAt: new Date().toISOString(),
    };

    // Update completed missions list if not already completed
    const completedMissions = userProgress.completedMissions.includes(missionId)
      ? userProgress.completedMissions
      : [...userProgress.completedMissions, missionId];

    // Update mission results
    const missionResultsList = [
      ...(userProgress.missionResults || []),
      missionResult
    ];

    // Update statistics
    const statistics = {
      ...userProgress.statistics,
      totalMissionsCompleted: completedMissions.length,
      // Assuming mission completion implies challenge completion for stats
      totalChallengesCompleted: (userProgress.statistics.totalChallengesCompleted || 0) + 1,
      averageAccuracy: calculateNewAverageAccuracy(
        userProgress.statistics.averageAccuracy,
        userProgress.statistics.totalChallengesCompleted,
        accuracy // Use accuracy from results
      ),
      bestScore: Math.max(userProgress.statistics.bestScore, score),
      lastActive: new Date().toISOString(),
    };

    // Update the user progress
    updateProgress({
      completedMissions,
      missionResults: missionResultsList,
      statistics
    });
    
    // Check for achievements is handled within updateProgress
  };

  /**
   * Calculate new average accuracy after completing a challenge
   */
  const calculateNewAverageAccuracy = (
    currentAverage: number,
    totalChallenges: number,
    newAccuracy: number
  ): number => {
    // If no previous challenges, return new accuracy
    if (totalChallenges === 0) return newAccuracy;
    
    // Calculate new average
    const totalAccuracy = currentAverage * totalChallenges;
    return (totalAccuracy + newAccuracy) / (totalChallenges + 1);
  };

  /**
   * Reset all user progress to default values
   */
  const resetProgress = () => {
    localStorageService.clearUserProgress();
    const defaultProgress = localStorageService.getUserProgress();
    setUserProgress({
      ...defaultProgress,
      completedChallenges: []
    });
    setUnlockedAchievements([]);
  };

  /**
   * Update a specific skill level
   * 
   * @param skillName Name of the skill to update
   * @param amount Amount to increase the skill level by
   */
  const updateSkill = (skillName: keyof SkillLevels, amount: number) => {
    const currentSkillLevel = userProgress.skillLevels[skillName] || 0;
    
    // Handle the case where currentSkillLevel might be a PieceMoveSkills object
    if (typeof currentSkillLevel === 'object') {
      // Skip updating if it's an object (this would be handled by updatePieceSkill instead)
      return;
    }
    
    const newSkillLevel = Math.min(100, currentSkillLevel + amount);
    
    updateProgress({
      skillLevels: {
        ...userProgress.skillLevels,
        [skillName]: newSkillLevel
      }
    });
  };

  /**
   * Complete a challenge and record it in the progress
   * 
   * @param challenge Challenge completion data
   */
  const completeChallenge = (challenge: Challenge) => {
    // Update completed challenges list if not already completed
    const completedChallenges = userProgress.completedChallenges || [];
    const isChallengeCompleted = completedChallenges.some(c => c.id === challenge.id);

    if (!isChallengeCompleted) {
      const updatedChallenges = [...completedChallenges, challenge];
      
      // Update statistics
      const statistics = {
        ...userProgress.statistics,
        totalChallengesCompleted: (userProgress.statistics.totalChallengesCompleted || 0) + 1,
        bestScore: Math.max(userProgress.statistics.bestScore, challenge.score),
        lastActive: new Date().toISOString(),
      };
      
      // Just update the local state directly
      setUserProgress(prev => ({
        ...prev,
        completedChallenges: updatedChallenges,
        statistics
      }));
    }
  };

  /**
   * Collect all skill updates for a move attempt to apply in one batch
   * to avoid multiple updateProgress calls
   */
  const collectMoveSkillUpdates = (
    moveAttempt: MoveAttempt,
    currentSkills: SkillLevels
  ): Partial<SkillLevels> => {
    if (!moveAttempt.isSuccessful) return {};
    
    const { pieceType, moveType } = moveAttempt;
    const skillUpdates: Partial<SkillLevels> = {};
    
    // Calculate new skill levels
    if (moveType === 'castling') {
      const castlingSkill = currentSkills.castling || 0;
      skillUpdates.castling = Math.min(100, castlingSkill + 1);
    } else if (moveType === 'enPassant') {
      const enPassantSkill = currentSkills.enPassant || 0;
      skillUpdates.enPassant = Math.min(100, enPassantSkill + 1);
    } else if (moveType === 'promotion') {
      const promotionSkill = currentSkills.pawnPromotion || 0;
      skillUpdates.pawnPromotion = Math.min(100, promotionSkill + 1);
    }
    
    // Update basic moves skill
    const basicMovesSkill = currentSkills.basicMoves || 0;
    skillUpdates.basicMoves = Math.min(100, basicMovesSkill + 0.2);
    
    // Update piece-specific skill
    const currentPieceMoves = currentSkills.pieceMoves || DEFAULT_PIECE_MOVES;
    const pieceSkill = currentPieceMoves[pieceType] || 1;
    
    skillUpdates.pieceMoves = {
      ...currentPieceMoves,
      [pieceType]: Math.min(100, pieceSkill + 0.5)
    };
    
    return skillUpdates;
  };

  /**
   * Track a move attempt and update move skill metrics
   * 
   * @param moveAttempt Data about the attempted move
   */
  const trackMoveAttempt = (moveAttempt: MoveAttempt) => {
    const { pieceType, moveType, isSuccessful, timeSpent, usedHint } = moveAttempt;
    
    // Get current move metrics
    const currentMetrics = userProgress.moveSkillMetrics || {
      totalMoves: { attempted: 0, successful: 0 },
      movesByType: {
        normal: { attempted: 0, successful: 0 },
        castling: { attempted: 0, successful: 0 },
        enPassant: { attempted: 0, successful: 0 },
        promotion: { attempted: 0, successful: 0 },
      },
      movesByPiece: {
        pawn: { attempted: 0, successful: 0 },
        knight: { attempted: 0, successful: 0 },
        bishop: { attempted: 0, successful: 0 },
        rook: { attempted: 0, successful: 0 },
        queen: { attempted: 0, successful: 0 },
        king: { attempted: 0, successful: 0 },
      },
      averageTimePerMove: 0,
      hintsUsed: 0,
    };
    
    // Create a deep copy to avoid mutating the original state directly
    const updatedMetrics = JSON.parse(JSON.stringify(currentMetrics));

    // Update total moves
    updatedMetrics.totalMoves.attempted = (updatedMetrics.totalMoves.attempted || 0) + 1;
    if (isSuccessful) {
      updatedMetrics.totalMoves.successful = (updatedMetrics.totalMoves.successful || 0) + 1;
    }
    
    // Update moves by type
    if (!updatedMetrics.movesByType[moveType]) {
      updatedMetrics.movesByType[moveType] = { attempted: 0, successful: 0 };
    }
    updatedMetrics.movesByType[moveType].attempted += 1;
    if (isSuccessful) {
      updatedMetrics.movesByType[moveType].successful += 1;
    }
    
    // Update moves by piece
    if (!updatedMetrics.movesByPiece[pieceType]) {
      updatedMetrics.movesByPiece[pieceType] = { attempted: 0, successful: 0 };
    }
    updatedMetrics.movesByPiece[pieceType].attempted += 1;
    if (isSuccessful) {
      updatedMetrics.movesByPiece[pieceType].successful += 1;
    }
    
    // Update average time per move
    const totalMoves = updatedMetrics.totalMoves.attempted;
    if (totalMoves > 0) {
      const currentTotalTime = (updatedMetrics.averageTimePerMove || 0) * (totalMoves - 1);
      updatedMetrics.averageTimePerMove = (currentTotalTime + timeSpent) / totalMoves;
    } else {
      updatedMetrics.averageTimePerMove = timeSpent; // First move
    }
    
    // Track hint usage
    if (usedHint) {
      updatedMetrics.hintsUsed = (updatedMetrics.hintsUsed || 0) + 1;
    }
    
    // Collect all skill updates to apply in one batch
    const skillUpdates = collectMoveSkillUpdates(moveAttempt, userProgress.skillLevels);
    
    // Save all updates in one go
    updateProgress({
      moveSkillMetrics: updatedMetrics,
      skillLevels: Object.keys(skillUpdates).length > 0 ? 
        { ...userProgress.skillLevels, ...skillUpdates } : 
        undefined
    });
  };
  
  /**
   * Update the skill level for a specific piece
   * 
   * @param pieceType The type of piece
   * @param amount Amount to increase
   */
  const updatePieceSkill = (pieceType: 'pawn' | 'knight' | 'bishop' | 'rook' | 'queen' | 'king', amount: number) => {
    // Get current piece move levels
    const currentLevels = userProgress.skillLevels.pieceMoves || DEFAULT_PIECE_MOVES;
    
    // Update the specified piece skill
    const pieceSkill: number = currentLevels[pieceType];
    const newValue = Math.min(100, pieceSkill + amount);
    
    // Update the skill levels
    updateProgress({
      skillLevels: {
        ...userProgress.skillLevels,
        pieceMoves: {
          ...currentLevels,
          [pieceType]: newValue
        }
      }
    });
  };
  
  /**
   * Get the skill level for a specific piece and/or move type
   * 
   * @param pieceType The type of chess piece
   * @param moveType Optional specific move type
   * @returns The skill level
   */
  const getMoveSkillLevel = (
    pieceType: 'pawn' | 'knight' | 'bishop' | 'rook' | 'queen' | 'king',
    moveType?: 'normal' | 'castling' | 'enPassant' | 'promotion'
  ): number => {
    if (moveType) {
      // Return specific move type skill
      switch (moveType) {
        case 'castling':
          return userProgress.skillLevels.castling || 1;
        case 'enPassant':
          return userProgress.skillLevels.enPassant || 1;
        case 'promotion':
          return userProgress.skillLevels.pawnPromotion || 1;
        default:
          return userProgress.skillLevels.basicMoves || 1;
      }
    } else {
      // Return piece-specific skill
      return userProgress.skillLevels.pieceMoves?.[pieceType] || 1;
    }
  };
  
  /**
   * Update a specific move skill
   * 
   * @param pieceType The type of chess piece
   * @param moveType The type of move
   * @param amount Amount to increase the skill
   */
  const updateMoveSkill = (
    pieceType: 'pawn' | 'knight' | 'bishop' | 'rook' | 'queen' | 'king',
    moveType: 'normal' | 'castling' | 'enPassant' | 'promotion',
    amount: number
  ) => {
    if (moveType === 'castling' || moveType === 'enPassant' || moveType === 'promotion') {
      const skillName = moveType === 'promotion' ? 'pawnPromotion' : moveType;
      updateSkill(skillName as keyof SkillLevels, amount);
    } else {
      updatePieceSkill(pieceType, amount);
    }
  };
  
  /**
   * Get all move skill metrics
   * 
   * @returns The move skill metrics
   */
  const getMoveSkillMetrics = (): MoveSkillMetrics => {
    return userProgress.moveSkillMetrics || {
      totalMoves: { attempted: 0, successful: 0 },
      movesByType: {
        normal: { attempted: 0, successful: 0 },
        castling: { attempted: 0, successful: 0 },
        enPassant: { attempted: 0, successful: 0 },
        promotion: { attempted: 0, successful: 0 },
      },
      movesByPiece: {
        pawn: { attempted: 0, successful: 0 },
        knight: { attempted: 0, successful: 0 },
        bishop: { attempted: 0, successful: 0 },
        rook: { attempted: 0, successful: 0 },
        queen: { attempted: 0, successful: 0 },
        king: { attempted: 0, successful: 0 },
      },
      averageTimePerMove: 0,
      hintsUsed: 0,
    };
  };

  /**
   * Updates a sequence knowledge item with a new performance grade
   * and persists it via the knowledge item service.
   * 
   * This indirectly updates tactical theme proficiency by updating the
   * source data that tacticalProficiency is derived from.
   * 
   * @param item The knowledge item to update
   * @param grade The SM-2 performance grade (0-5)
   */
  const completeSequenceChallenge = (item: KnowledgeItem, grade: SuperMemoGrade): void => {
    // Validate input
    if (!item || item.type !== 'sequence') {
      console.error('Invalid item passed to completeSequenceChallenge. Must be a sequence type.', item);
      return;
    }
    
    if (grade < 0 || grade > 5) {
      console.error('Invalid grade passed to completeSequenceChallenge. Must be 0-5.', grade);
      return;
    }
    
    try {
      // Update the knowledge item using the SM-2 algorithm
      const updatedItem = updateKnowledgeItemReview(item, grade);
      
      // Persist the updated item
      knowledgeItemService.saveKnowledgeItem(updatedItem);
      
      // Also update the sequences skill level based on the grade
      // This ensures the general sequences skill level is updated as well,
      // not just the tactical theme proficiency
      const skillAmount = grade > 3 ? 0.1 : 0.05; // Larger increase for better performance
      updateSkill('sequences', skillAmount);
      
      // Note: tacticalProficiency will be recalculated automatically via useMemo
      // when LearningContext's knowledgeItems are updated
    } catch (error) {
      console.error('Error completing sequence challenge:', error);
    }
  };

  // Context value that will be provided to consumers
  const contextValue: ProgressContextType = {
    userProgress,
    updateProgress,
    completeMission,
    updateSkill,
    completeChallenge,
    resetProgress,
    isLoading,
    unlockedAchievements,
    checkForNewAchievements,
    trackMoveAttempt,
    getMoveSkillLevel,
    updateMoveSkill,
    getMoveSkillMetrics,
    completeSequenceChallenge,
    detailedSkillDistribution,
    weakAreas,
    tacticalProficiency,
  };

  return (
    <ProgressContext.Provider value={contextValue}>
      {children}
    </ProgressContext.Provider>
  );
};

/**
 * Custom hook to use the progress context
 */
export const useProgress = (): ProgressContextType => {
  const context = useContext(ProgressContext);
  if (context === undefined) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};

export default ProgressContext; 